<template>
  <div id="seeds-table">
    <!------------------------- Table  starts------------------------->
    <v-data-table
      show-expand
      single-expand
      :items="data"
      class="body-2"
      :headers="headers"
      :loading="isLoading"
      :footer-props="footerProps"
      @current-items="currentItems"
      @item-expanded="$emit('fetch-logs', $event)"
      :options.sync="pagination"
      :items-per-page="paginationMeta.itemsPerPage"
      :loading-text="$dictionary.app.common.seeds.loading"
      :no-data-text="$dictionary.app.seeds.index.noResults"
      :server-items-length="paginationMeta.serverItemsLength"
    >
      <template v-slot:expanded-item="{ headers }">
        <slot name="expanded-seed" :headers="headers" />
      </template>
      <template #item.data-table-expand="{ item, expand, isExpanded }">
        <expand-row-btn
          :expand="expand"
          :isExpanded="isExpanded"
          :isLoading="showLogsLoader(item)"
        />
      </template>
      <template #item.seed_address="{ item }">
        <div class="d-flex align-center">
          <common-icons
            classes="mr-1"
            v-if="hasError(item)"
            :iconType="$appConfig.commonIcons.error"
          />
          <div class="body-2 text--primary">{{ item.seed_address }}</div>
        </div>
      </template>
      <template #item.status="{ item }">
        <span class="black--text">
          <v-chip
            small
            outlined
            :color="chipColor(item)"
            class="mt-n1 status-switch-btn"
          >
            {{ item.status | capitalize }}
          </v-chip>
        </span>
      </template>

      <template #item.actions="{ item }">
        <v-menu dense offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-on="on">
              <v-icon v-bind="attrs">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item @click="editSeed(item)">
              <v-icon class="mr-3"> mdi-pencil </v-icon>
              <v-list-item-title class="edit-seed body-2 text-black--80 pr-3">
                Edit Seed
              </v-list-item-title>
            </v-list-item>

            <v-list-item @click="onDisableSeed(true, item)">
              <v-icon
                class="mr-3 text--secondary"
                :class="
                  isSeedActive(item)
                    ? 'material-icons-round'
                    : 'material-icons-outlined'
                "
              >
                {{
                  isSeedActive(item) ? "highlight_off" : "check_circle_outline"
                }}
              </v-icon>

              <v-list-item-title class="delete-seed body-2 text-primary pr-3">
                {{ isSeedActive(item) ? "Archive Seed" : "Re-Activate Seed" }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleteSeed(item)">
              <v-icon class="mr-3"> mdi-delete </v-icon>
              <v-list-item-title class="delete-seed body-2 text-primary pr-3">
                Delete Seed
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <!------------------------- Table ends ------------------------->
    <disable-seed-dialog
      :title="seedDialog.title"
      :dialog="seedDialog.dialog"
      @close="onDisableSeed(false)"
      :is-loading="isSeedDisabling"
      :sub-title="seedDialog.subTitle"
      @delete="disableSeed(seedDialog.seed)"
      :btn-text="isSeedActive(seedDialog.seed) ? 'Archive' : 'Re-activate'"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { updateSeed } from "@/services";
import { PaginationSortMixin } from "@/mixins";

import { isResponseCode } from "@/utils/common.utils";
import { CommonIcons } from "@/components/shared";
import { RESPONSE_CODES, SEED_STATUSES, STATUS_CHIP_COLORS } from "@/constants";

import dayjs from "dayjs";
import ExpandRowBtn from "@/components/shared/ExpandRowBtn.vue";
import { DeleteRecordDialog as DisableSeedDialog } from "@/components/shared";

const [ACTIVE, ARCHIVED] = Object.values(SEED_STATUSES);

/**
 * Seeds listing table
 */
export default {
  name: "SeedsTable",
  /**
   *  ---------------- Mixins ------------------
   */
  mixins: [PaginationSortMixin],
  /**
   *  ---------------- Components ------------------
   */
  components: {
    CommonIcons,
    ExpandRowBtn,
    DisableSeedDialog,
  },
  /**
   *  ---------------- Props ------------------
   */
  props: {
    data: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    updateRecords: { type: Boolean, required: true, default: false },
    areLogsLoading: { type: Boolean, required: true, default: false },
    activeItem: { type: Object, required: true, default: () => ({}) },
  },
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["sorted-records", "edit-seed", "delete-seed"],

  /**
   * ---------------- Data properties ------------------
   */
  data() {
    const { snackbarTypes } = this.$appConfig.snackbar;
    const { title, subTitle } = this.$dictionary.app.seeds.disableSeed;

    return {
      snackbarTypes,
      defaultSortOrder: "seed_address",
      sortDesc: false,
      isSeedDisabling: false,
      seedDialog: { dialog: false, title, subTitle },
    };
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * isSeedActive
     * @description Is seed status active or not
     * @returns {Boolean}
     */
    isSeedActive(seed = {}) {
      const { status } = seed;
      return status === ACTIVE;
    },
    /**
     * Maps vuex store methods in the component
     */
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * Determines if a seed address has an error or not
     * @returns {boolean} Has error or not
     */
    hasError({ readAt, failedAt }) {
      if (readAt && failedAt) {
        return dayjs(failedAt).isAfter(dayjs(readAt));
      } else if (failedAt) {
        return dayjs(failedAt).isValid();
      }
      return false;
    },
    /**
     * Show loader to the user
     */
    showLogsLoader({ seed_address }) {
      return (
        this.areLogsLoading && seed_address === this.activeItem.seed_address
      );
    },
    /**
     * Emits a custom event with sorted items
     * @param {Array} items Sorted items
     */
    currentItems(items) {
      this.$emit("sorted-records", [...items]);
    },
    /**
     * chipColor
     * @param {String} status seed status
     * @returns {String} color of the chip on the basis of status
     */
    chipColor({ status }) {
      let DEFAULT_COLOR = STATUS_CHIP_COLORS.disabled;
      const [active, disabled, bounced] = Object.keys(STATUS_CHIP_COLORS);

      switch (status) {
        case active:
          return STATUS_CHIP_COLORS.active;
        case disabled:
          return STATUS_CHIP_COLORS.disabled;
        case bounced:
          return STATUS_CHIP_COLORS.bounced;

        default:
          return DEFAULT_COLOR;
      }
    },
    /**
     * Edit seed
     * @description Emits event in parent component to edit the seed
     * @emits edit-seed
     */
    editSeed(seed) {
      this.$emit("edit-seed", seed);
    },
    /**
     * Delete seed
     * @description Emits event in parent component to delete the seed
     * @emits delete-seed
     */
    deleteSeed(seed) {
      this.$emit("delete-seed", seed);
    },
    /**
     * Open and closes disable dialog box
     * And computes the title of the dialog box including seed email address
     */
    onDisableSeed(val, seed = null) {
      const REPLACER = "<<email>>";
      const { disableSeed, enableSeed } = this.$dictionary.app.seeds;
      const isSeedActive = seed ? this.isSeedActive(seed) : false;
      if (val) {
        const { seed_address } = seed;
        this.seedDialog = {
          ...this.seedDialog,
          seed,
          dialog: true,
          subTitle: isSeedActive ? disableSeed.subTitle : enableSeed.subTitle,
          title: isSeedActive
            ? disableSeed.title.replace(REPLACER, seed_address)
            : enableSeed.title.replace(REPLACER, seed_address),
        };
      } else this.seedDialog.dialog = false;
    },
    /**
     * seedSuccessMessage
     * @description seed succes message to be shown in the snackbar
     */
    seedSuccessMessage(status) {
      return this.isSeedActive({ status })
        ? this.$dictionary.app.seeds.enableSeed.successMessage
        : this.$dictionary.app.seeds.disableSeed.successMessage;
    },
    /**
     * Disable seed
     */
    async disableSeed({ id, status: seedStatus }) {
      try {
        this.isSeedDisabling = true;
        seedStatus = seedStatus === ACTIVE ? ARCHIVED : ACTIVE;
        const payload = { status: seedStatus, id };
        const { status } = await updateSeed(payload);

        // Shows success message if api response status is 200
        if (isResponseCode(status, RESPONSE_CODES.success)) {
          this.setSnackbar({
            value: true,
            message: this.seedSuccessMessage(seedStatus),
            type: this.snackbarTypes.success,
          });
        }
        this.onDisableSeed(false);
        this.getRecords();
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.$errorMessage(error),
          type: this.snackbarTypes.error,
        });
      } finally {
        this.isSeedDisabling = false;
      }
    },
  },
  /**
   * -------------- Watching properties -------------
   */
  watch: {
    /**
     * Updates the list of seeds
     */
    updateRecords(val) {
      if (val) this.getRecords();
    },
  },
  /**
   * -------------- Created lifecycle hook -------------
   */
  created() {
    // Emits get-recorsd event in the parent component to fetch the records
    this.getRecords();
  },
};
</script>
<style lang="scss">
#seeds-table {
  tr.v-data-table__expanded {
    box-shadow: none !important;
  }
}
</style>
